import { I18n } from "@assets/strings";
import { debounce } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { createToast } from "@ui/toasts/utils";
import { Tag } from "@ui/components/inputs/tagsInput/internalLogic/components/Tag";
import { classSelectors } from "@ui/components/inputs/tagsInput/internalLogic/utils/selectors";

type Tags = string[];

export interface ReactTagInputProps {
	tags: Tags;
	onChange: (tags: Tags) => void;
	onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	placeholder?: string;
	maxTags?: number;
	validator?: (val: string) => boolean;
	editable?: boolean;
	readOnly?: boolean;
	removeOnBackspace?: boolean;
	emitInput: (input: string) => void;
	suggestions: string[];
}

export const ReactTagInput: React.FC<ReactTagInputProps> = ({
	onFocus,
	tags,
	placeholder,
	maxTags,
	editable,
	readOnly,
	onChange,
	emitInput,
	removeOnBackspace,
	validator,
}) => {
	const [input, setInput] = useState("");
	const [orderInput, setOrderInput] = useState(input);
	const [isEmailValid, setIsEmailValid] = useState(true);
	const EMAILSEPARATOR = ",";

	const inputRef = useRef(null);
	const setServicesValueDebounced = useRef(debounce(setOrderInput, 200));
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value.includes(EMAILSEPARATOR)) {
			addTag(input);
		}

		if (!e.target.value || !e.target.value.includes(EMAILSEPARATOR)) {
			setIsEmailValid(true);
		}

		setInput(e.target.value);
	};

	useEffect(() => {
		setInput("");
	}, [tags]);

	useEffect(() => {
		setServicesValueDebounced.current(input);
		if (orderInput) {
			emitInput(orderInput);
		}
	}, [input]);
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		// On enter
		if (e.keyCode === 13) {
			// Prevent form submission if tag input is nested in <form>
			e.preventDefault();

			// If input is blank, do nothing
			if (input === "") {
				return;
			}

			// Check if input is valid
			const valid = validator !== undefined ? validator(input) : true;
			if (!valid) {
				return;
			}

			// Add input to tag list
			addTag(input);
		}
		// On backspace or delete
		else if (removeOnBackspace && (e.keyCode === 8 || e.keyCode === 46)) {
			// If currently typing, do nothing
			if (input !== "") {
				return;
			}

			// If input is blank, remove previous tag
			removeTag(tags.length - 1);
		}
	};

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const addTag = (email: string) => {
		if (!checkInputEmailIsValid(input)) {
			createToast(I18n.t("error"), I18n.t("profile.card.invite.action.email_invalid_toast"), "error");
			return;
		}
		const oldEmailAdded = [...tags];
		oldEmailAdded.push(email.trim().toLowerCase().replace(/\s/g, ""));
		onChange(oldEmailAdded);
		setInput("");
		setIsEmailValid(true);
	};
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const removeTag = (i: number) => {
		const tagsR = [...tags];
		tagsR.splice(i, 1);
		onChange(tagsR);
	};

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const updateTag = (i: number, value: string) => {
		const tagsU = [...tags];
		tagsU[i] = value;
		onChange(tagsU);
	};

	function checkInputEmailIsValid(email: string): boolean {
		const valid = validator !== undefined ? validator(email) : true;
		setIsEmailValid(valid);
		return valid;
	}

	const maxTagsReached = maxTags !== undefined ? tags.length >= maxTags : false;

	const isEditable = readOnly ? false : editable || false;

	const showInput = !readOnly && !maxTagsReached;

	return (
		<>
			<div className={classSelectors.wrapper}>
				{tags.map((tag, i) => (
					<Tag
						key={i}
						value={tag}
						index={i}
						editable={isEditable}
						readOnly={readOnly || false}
						inputRef={inputRef}
						update={updateTag}
						remove={removeTag}
						validator={validator}
						removeOnBackspace={removeOnBackspace}
					/>
				))}
				{showInput && (
					<input
						ref={inputRef}
						value={input}
						className={classSelectors.input}
						placeholder={placeholder || "Type and press enter"}
						onChange={handleChange}
						onKeyDown={onInputKeyDown}
						onFocus={onFocus}
						onBlur={() => {
							if (input) {
								addTag(input);
							}
						}}
					/>
				)}
			</div>
			{!isEmailValid && <InvalidEmail>{I18n.t("profile.card.invite.action.email_invalid_example")}</InvalidEmail>}
		</>
	);
};

const InvalidEmail = styled.div`
	margin-top: 5px;
	font-family: Sora;
	font-style: italic;
	font-weight: 300;
	font-size: 10px;
	${({ theme }) => `color:${theme.colors.red}`}
`;
